import { fabric } from 'fabric';

// Default placement: small, on the left edge, vertical (rotated -90°), centered.
export const BRANDING_DEFAULTS = {
  xPercent: 3,
  yPercent: 50,
  rotation: -90,
  scalePercent: 8,
};

// Recolor a logo's opaque pixels to the given hex color.
export function tintImage(url, color) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const c = document.createElement('canvas');
      c.width = img.naturalWidth;
      c.height = img.naturalHeight;
      const ctx = c.getContext('2d');
      ctx.drawImage(img, 0, 0);
      ctx.globalCompositeOperation = 'source-in';
      ctx.fillStyle = color || '#FFFFFF';
      ctx.fillRect(0, 0, c.width, c.height);
      resolve(c.toDataURL('image/png'));
    };
    img.onerror = reject;
    img.src = url;
  });
}

// Add the branding logo to a fabric canvas. `position` is { xPercent,
// yPercent, rotation, scalePercent } — any missing fields fall back to
// BRANDING_DEFAULTS. `interactive` controls whether the admin can
// drag/resize/rotate the logo on the canvas.
export async function addBrandingLogoToCanvas(canvas, imageUrl, color, naturalW, naturalH, position = {}, interactive = false) {
  if (!imageUrl) return null;
  const existing = canvas.getObjects().find(o => o._isBrandingLogo);
  if (existing) canvas.remove(existing);

  const xPercent = position.xPercent ?? BRANDING_DEFAULTS.xPercent;
  const yPercent = position.yPercent ?? BRANDING_DEFAULTS.yPercent;
  const rotation = position.rotation ?? BRANDING_DEFAULTS.rotation;
  const scalePercent = position.scalePercent ?? BRANDING_DEFAULTS.scalePercent;

  const tintedUrl = await tintImage(imageUrl, color);
  return new Promise(resolve => {
    fabric.Image.fromURL(tintedUrl, (img) => {
      const targetWidth = (scalePercent / 100) * naturalW;
      const scale = img.width ? targetWidth / img.width : 1;
      img.set({
        left: (xPercent / 100) * naturalW,
        top: (yPercent / 100) * naturalH,
        originX: 'center',
        originY: 'center',
        scaleX: scale,
        scaleY: scale,
        angle: rotation,
        opacity: 1,
        selectable: interactive,
        evented: interactive,
        hasControls: interactive,
        lockUniScaling: true,
      });
      img._isBrandingLogo = true;
      canvas.add(img);
      canvas.sendToBack(img);
      canvas.requestRenderAll();
      resolve(img);
    }, { crossOrigin: 'anonymous' });
  });
}

// Read back the current logo's position from the canvas as { xPercent,
// yPercent, rotation, scalePercent } so it can be persisted to the product.
export function readBrandingLogoPosition(canvas, naturalW, naturalH) {
  const img = canvas.getObjects().find(o => o._isBrandingLogo);
  if (!img) return null;
  const targetWidth = img.width * (img.scaleX || 1);
  return {
    xPercent: (img.left / naturalW) * 100,
    yPercent: (img.top / naturalH) * 100,
    rotation: img.angle || 0,
    scalePercent: (targetWidth / naturalW) * 100,
  };
}
